import {
  workerPaySurveys,
  type WorkerPaySurveysDto,
} from "@clipboard-health/contract-worker-app-bff";
import { isDefined } from "@clipboard-health/util-ts";
import { get } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useWorkerGeoLocation } from "@src/appV2/Worker/useWorkerGeoLocation";
import { useQuery, type UseQueryOptions } from "@tanstack/react-query";

import { workerPaySurveyQueryKeys } from "./queryKeys";

const WORKER_PAY_SURVEYS_PATH = "/worker-pay-surveys";
const WORKER_PAY_SURVEYS_URL = `${environmentConfig.REACT_APP_WORKER_APP_BFF_URL}${WORKER_PAY_SURVEYS_PATH}`;

interface UseGetWorkerPaySurveysOptions extends Omit<
  UseQueryOptions<WorkerPaySurveysDto>,
  "queryKey" | "queryFn"
> {
  pageSize?: number;
}

export function useGetWorkerPaySurveys(options: UseGetWorkerPaySurveysOptions = {}) {
  const { pageSize = 1, enabled, ...restOptions } = options;
  const { userId } = useDefinedWorker();
  const workerGeoLocation = useWorkerGeoLocation();

  const latitude = workerGeoLocation?.latitude;
  const longitude = workerGeoLocation?.longitude;

  const isEnabled = enabled !== false && isDefined(workerGeoLocation);

  return useQuery<WorkerPaySurveysDto>({
    queryKey: workerPaySurveyQueryKeys.workerPaySurveys(userId),
    queryFn: async () => {
      const response = await get({
        url: WORKER_PAY_SURVEYS_URL,
        queryParams: {
          filter: {
            "worker.latitude": latitude!,
            "worker.longitude": longitude!,
          },
          page: {
            size: pageSize,
          },
        },
        responseSchema: workerPaySurveys,
      });

      return response.data;
    },
    enabled: isEnabled,
    staleTime: 10 * 60 * 1000, // 10 minutes
    ...restOptions,
  });
}
